import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ResponsiveFormLayout from '../../../shared/components/layout/ResponsiveFormLayout';
import { useLanguage } from '../../../shared/contexts/LanguageContext';
import { useProgressWithAutoRefresh } from '../../../shared/contexts/ProgressContext';
import { useStepRedirect, skipNextStepRedirect } from '../../../hooks/useStepRedirect';
import { FaCheck } from "react-icons/fa6";
import { FaExclamationTriangle } from "react-icons/fa";
import { ClosureApiService } from '../../closure/api/ClosureApiService';

interface PledgeTerms {
  id: string;
  textAr: string;
  textEn: string;
  checked: boolean;
  required: boolean;
}

const ClosureAgreeTermsScreen: React.FC = () => {
  useStepRedirect();

  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [checkingProgress, setCheckingProgress] = useState(true);
  const [canProceed, setCanProceed] = useState(true);
  const [progressWarning, setProgressWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedData = React.useRef(false);
  const closureApi = React.useMemo(() => new ClosureApiService(), []);

  const { progress } = useProgressWithAutoRefresh();

  const [pledgeTerms, setPledgeTerms] = useState<PledgeTerms[]>([
    {
      id: 'surrender_license',
      textAr: 'أتعهد بتسليم إجازة فتح العيادة الأصلية إلى النقابة.',
      textEn: 'I pledge to surrender the original clinic opening license to the syndicate.',
      checked: false,
      required: true
    }
  ]);

  // Check doctor's closure progress when progress data is available
  useEffect(() => {
    if (!progress || hasLoadedData.current) {
      return;
    }

    hasLoadedData.current = true;
    setCheckingProgress(true);

    try {
      // Check active closure request stage
      const closureRequest = progress.active_requests?.find(
        (r: any) => r.request_type === 'closure'
      );
      const currentStage = closureRequest?.current_stage;

      // Block if they've already completed this step (payment stage or beyond)
      if (currentStage === 'payment' || currentStage === 'inspection' || currentStage === 'approval') {
        setCanProceed(false);
        setProgressWarning(t('terms_already_agreed'));
      } else {
        setCanProceed(true);
        setProgressWarning(null);
      }
    } catch (err) {
      console.error('Error checking closure progress:', err);
      setCanProceed(true);
    } finally {
      setCheckingProgress(false);
    }
  }, [progress, t]);

  const handleTermChange = (termId: string, checked: boolean) => {
    setPledgeTerms(prev =>
      prev.map(term =>
        term.id === termId ? { ...term, checked } : term
      )
    );
  };

  const allRequiredTermsChecked = pledgeTerms.filter(term => term.required).every(term => term.checked);

  const handleSubmit = async () => {
    if (!allRequiredTermsChecked) {
      setError(t('agree_to_all_pledges'));
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoading(true);

    try {
      console.log('✅ Closure terms accepted, calling backend API...');

      const closureRequestId = progress?.active_requests?.find(
        (r: any) => r.request_type === 'closure'
      )?.request_id;
      if (!closureRequestId) {
        setError(t('no_active_closure') || 'No active closure request found.');
        return;
      }
      const result = await closureApi.agreeToTerms(true, closureRequestId);

      if (!result.isSuccess) {
        setError(result.error || t('failed_closure_agreement'));
        setTimeout(() => setError(null), 3000);
        return;
      }

      console.log('✅ Backend updated, proceeding to payment...');

      // Navigate to payment with closure context
      const closureRequest = progress?.active_requests?.find(
        (r: any) => r.request_type === 'closure'
      );

      skipNextStepRedirect();
      navigate('/payment', {
        state: {
          requestType: 'closure',
          requestId: closureRequest?.request_id,
        },
      });
    } catch (err) {
      console.error('❌ Closure terms submission error:', err);
      setError(err instanceof Error ? err.message : t('failed_proceed_payment'));
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    skipNextStepRedirect();
    navigate('/closure-review');
  };

  const actionButtons = (
    <div className="flex gap-3 w-full">
      <button
        onClick={handleBack}
        disabled={loading || checkingProgress}
        className="min-h-[56px] px-6 py-4 rounded-2xl text-base font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200"
      >
        {t('back') || 'Back'}
      </button>
      <button
        onClick={handleSubmit}
        disabled={loading || !allRequiredTermsChecked || !canProceed || checkingProgress}
        className={`flex-1 min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
          loading || !allRequiredTermsChecked || !canProceed || checkingProgress
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:opacity-90 cursor-pointer shadow-md hover:shadow-lg'
        }`}
        style={{ backgroundColor: '#2E5D7D' }}
      >
        {loading || checkingProgress ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
            <span>{checkingProgress ? t('checking') : t('loading')}</span>
          </div>
        ) : (
          t('continue')
        )}
      </button>
    </div>
  );

  return (
    <ResponsiveFormLayout
      onBack={handleBack}
      showBackButton={true}
      title={t('close_clinic')}
      subtitle={t('closure_pledge')}
      actions={actionButtons}
      progressValue={85}
      className={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="space-y-6">
        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className={`text-red-800 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
              {error}
            </p>
          </div>
        )}

        {/* Surrender Plaque Reminder */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <FaExclamationTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h4 className="text-sm font-bold text-amber-900 mb-1">
                {t('surrender_plaque_note_title')}
              </h4>
              <p className="text-sm text-amber-800 leading-relaxed">
                {t('surrender_plaque_note_body')}
              </p>
            </div>
          </div>
        </div>

        {/* Pledge Terms Header */}
        <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {t('i_pledge_following')}
          </h3>
        </div>

        {/* Pledge Terms List */}
        <div className="space-y-4">
          {pledgeTerms.map((term) => (
            <div
              key={term.id}
              className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer hover:shadow-sm ${
                term.checked
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
              onClick={() => handleTermChange(term.id, !term.checked)}
            >
              <div className="flex-shrink-0 mt-1">
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                    term.checked
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {term.checked && (
                    <FaCheck className="w-3 h-3 text-white" />
                  )}
                </div>
              </div>

              <div className="flex-1">
                <p className={`text-gray-800 text-sm leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                  {isRTL ? term.textAr : term.textEn}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Registration Progress Warning */}
        {progressWarning && !canProceed && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <FaExclamationTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className={`text-red-800 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                {progressWarning}
              </p>
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        {!allRequiredTermsChecked && canProceed && !checkingProgress && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <p className={`text-amber-800 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('must_agree_all_pledges')}
              </p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {allRequiredTermsChecked && canProceed && !checkingProgress && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <FaCheck className="w-3 h-3 text-white" />
              </div>
              <p className={`text-green-800 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('all_pledges_accepted')}
              </p>
            </div>
          </div>
        )}
      </div>
    </ResponsiveFormLayout>
  );
};

export default ClosureAgreeTermsScreen;
